import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { ROUTES } from '@/routes/routes'
import { AuthLayout } from '@/components/auth/AuthLayout'

type State = 'loading' | 'success' | 'already_verified' | 'expired' | 'invalid' | 'error'

export default function DisavowSignupPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  // Initialise to 'invalid' immediately when there's no token — avoids setState inside effect
  const [state, setState] = useState<State>(() => (token ? 'loading' : 'invalid'))

  useEffect(() => {
    if (!token) return

    fetch('/api/v1/auth/email/disavow-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        if (res.ok) return setState('success')
        const body = await res.json().catch(() => ({}))
        const title: string = body.title ?? ''
        if (res.status === 409) return setState('already_verified')
        if (res.status === 410) return setState('expired')
        if (res.status === 404 || title.toLowerCase().includes('invalid'))
          return setState('invalid')
        setState('error')
      })
      .catch(() => setState('error'))
  }, [token])

  const copy: Record<State, { title: string; body: string; linkTo: string; linkLabel: string }> = {
    loading: {
      title: 'Removing account…',
      body: 'Please wait a moment.',
      linkTo: '',
      linkLabel: '',
    },
    success: {
      title: 'Account removed',
      body: "We've removed the signup attempt for your email. Your address is now free.",
      linkTo: ROUTES.AUTH.SIGN_UP,
      linkLabel: 'Sign up for LogMySpends',
    },
    already_verified: {
      title: 'Account is verified',
      body: "This account has already been verified and cannot be removed via this link. If you didn't create it, please contact support.",
      linkTo: ROUTES.AUTH.SIGN_IN,
      linkLabel: 'Sign in',
    },
    expired: {
      title: 'Link expired',
      body: 'This disavow link has expired (links are valid for 24 hours). If you still need to remove a signup, please sign up again and use the new link.',
      linkTo: ROUTES.AUTH.SIGN_UP,
      linkLabel: 'Back to sign up',
    },
    invalid: {
      title: 'Link not found',
      body: 'This link is invalid or has already been used.',
      linkTo: ROUTES.AUTH.SIGN_IN,
      linkLabel: 'Back to sign in',
    },
    error: {
      title: 'Link not found',
      body: 'This link is invalid or has already been used.',
      linkTo: ROUTES.AUTH.SIGN_IN,
      linkLabel: 'Back to sign in',
    },
  }
  const { title, body, linkTo, linkLabel } = copy[state]

  return (
    <AuthLayout title={title}>
      <p className="text-sm text-[var(--fl-text-2)] text-center">{body}</p>
      {linkTo && (
        <Link
          to={linkTo}
          className="block text-center text-sm font-medium text-[var(--fl-primary)] hover:underline"
        >
          {linkLabel}
        </Link>
      )}
    </AuthLayout>
  )
}
